import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import { api } from "@/lib/api";
import { HttpError } from "@/offline/cached";
import type { OverrideRequest } from "./override";

export type PloegPush = components["schemas"]["PloegPushDto"];
export type Conflict = components["schemas"]["ConflictDto"];
export type Chosen = components["schemas"]["ChosenAntwoordDto"];
export type AfwijkingSlot = components["schemas"]["AfwijkingSlotDto"];

/** The Api's `PushStatus` enum, serialised as an integer. */
export const PushStatus = { Pending: 0, Pushed: 1, Afwijking: 2, Failed: 3 } as const;

// Beheer is online only: no device copy, so a screen never acts on a stale answer.
async function ok<T>(call: Promise<{ data?: T; response: Response }>): Promise<T> {
  const { data, response } = await call;
  if (!response.ok) throw new HttpError(response.status);
  return data as T;
}

const beheerKey = (baanId: number) => ["beheer", baanId] as const;
const path = (baanId: number) => ({ params: { path: { id: baanId } } });

export const useVragen = (baanId: number) =>
  useQuery({
    queryKey: [...beheerKey(baanId), "jurylijsten"],
    queryFn: () => ok(api.GET("/api/banen/{id}/jurylijsten", path(baanId))),
  });

export const usePushStatus = (baanId: number) =>
  useQuery({ queryKey: [...beheerKey(baanId), "push"], queryFn: () => ok(api.GET("/api/banen/{id}/push", path(baanId))) });

export const useAfwijking = (baanId: number, uitslagId: number) =>
  useQuery({
    queryKey: [...beheerKey(baanId), "afwijking", uitslagId],
    queryFn: () => ok(api.GET("/api/banen/{id}/ploegen/{uitslagId}/afwijking", { params: { path: { id: baanId, uitslagId } } })),
  });

export const useNotities = (baanId: number) =>
  useQuery({ queryKey: [...beheerKey(baanId), "notities"], queryFn: () => ok(api.GET("/api/banen/{id}/notities", path(baanId))) });

export const useConflicten = (baanId: number) =>
  useQuery({
    queryKey: [...beheerKey(baanId), "conflicten"],
    queryFn: () => ok(api.GET("/api/banen/{id}/conflicten", path(baanId))),
  });

export const useChosen = (baanId: number) =>
  useQuery({ queryKey: [...beheerKey(baanId), "chosen"], queryFn: () => ok(api.GET("/api/banen/{id}/chosen", path(baanId))) });

/** Every beheer action can change the push status, the conflicten and the chosen antwoorden at once. */
function useBeheerMutation<TInput, TResult>(baanId: number, call: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: beheerKey(baanId) }),
        queryClient.invalidateQueries({ queryKey: ["baan"] }),
      ]),
  });
}

export const usePush = (baanId: number) =>
  useBeheerMutation(baanId, () => ok(api.POST("/api/banen/{id}/push", path(baanId))));

export const useResolveConflict = (baanId: number) =>
  useBeheerMutation(baanId, (body: { uitslagId: number; slot: string; antwoordId: string }) =>
    ok(api.POST("/api/banen/{id}/conflicten", { ...path(baanId), body })),
  );

export const useResolveAfwijking = (baanId: number) =>
  useBeheerMutation(baanId, (body: { uitslagId: number; keepVbs: boolean }) =>
    ok(api.POST("/api/banen/{id}/afwijkingen", { ...path(baanId), body })),
  );

export const useLockPloeg = (baanId: number) =>
  useBeheerMutation(baanId, ({ uitslagId, locked }: { uitslagId: number; locked: boolean }) => {
    const params = { params: { path: { id: baanId, uitslagId } } };
    return ok(
      locked
        ? api.POST("/api/banen/{id}/ploegen/{uitslagId}/lock", params)
        : api.POST("/api/banen/{id}/ploegen/{uitslagId}/unlock", params),
    );
  });

export const useLockBaan = (baanId: number) =>
  useBeheerMutation(baanId, (closeInVbs: boolean) => ok(api.POST("/api/banen/{id}/lock", { ...path(baanId), body: { closeInVbs } })));

export const useAssignments = (baanId: number) =>
  useQuery({
    queryKey: [...beheerKey(baanId), "assignments"],
    queryFn: () => ok(api.GET("/api/banen/{id}/assignments", path(baanId))),
  });

export const useAssign = (baanId: number) =>
  useBeheerMutation(baanId, (body: { contactId: number; positionId: number | null }) =>
    ok(api.POST("/api/banen/{id}/assignments", { ...path(baanId), body })),
  );

export const useSetAssignmentHidden = (baanId: number) =>
  useBeheerMutation(baanId, ({ assignmentId, hidden }: { assignmentId: string; hidden: boolean }) => {
    const params = { params: { path: { id: baanId, assignmentId } } };
    return ok(
      hidden
        ? api.POST("/api/banen/{id}/assignments/{assignmentId}/hide", params)
        : api.POST("/api/banen/{id}/assignments/{assignmentId}/unhide", params),
    );
  });

export const useRemoveAssignment = (baanId: number) =>
  useBeheerMutation(baanId, (assignmentId: string) =>
    ok(api.DELETE("/api/banen/{id}/assignments/{assignmentId}", { params: { path: { id: baanId, assignmentId } } })),
  );

export const useSendInloglinks = (baanId: number) =>
  useBeheerMutation(baanId, () => ok(api.POST("/api/banen/{id}/inloglinks", path(baanId))));

export const useSetOverride = (baanId: number) =>
  useBeheerMutation(baanId, (body: OverrideRequest) => ok(api.PUT("/api/banen/{id}/overrides", { ...path(baanId), body })));

// No invalidation: a shown code changes nothing a beheerder sees.
export const useShowInlogcode = (baanId: number) =>
  useMutation({
    mutationFn: (contactId: number) =>
      ok(api.POST("/api/banen/{id}/waarnemers/{contactId}/inlogcode", { params: { path: { id: baanId, contactId } } })),
  });

/** The status of a failed action, for the sentence next to its button. */
export const statusOf = (error: unknown) => (error instanceof HttpError ? error.status : 0);
